/**
 * Email + ICS calendar invites for public booking when the host has no OAuth calendar.
 * Mirrors next-email-client invite flow (ICS attachment + SES).
 */

import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

/**
 * All public bookings are scheduled in Central Time — `buildEventDetails`
 * (calendar-event-builder.ts) hardcodes `America/Chicago` for both the Google
 * and Microsoft calendar events. The human-readable email body must format the
 * appointment time in the SAME zone; otherwise it renders in the server's zone
 * (UTC on Vercel), so e.g. 9:00 AM CDT (14:00 UTC) would read as "2:00 PM".
 */
const BOOKING_TIMEZONE = "America/Chicago";

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function formatUtcForIcs(isoUtc: string): string {
  const date = new Date(isoUtc);
  return (
    `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(date.getUTCDate())}` +
    `T${pad2(date.getUTCHours())}${pad2(date.getUTCMinutes())}${pad2(date.getUTCSeconds())}Z`
  );
}

function foldIcsLine(line: string): string {
  if (line.length <= 75) return line;
  const segments: string[] = [];
  let index = 0;
  while (index < line.length) {
    const slice = line.slice(index, index + 75);
    segments.push(index === 0 ? slice : ` ${slice}`);
    index += 75;
  }
  return segments.join("\r\n");
}

export type PublicBookingInviteInput = {
  uid: string;
  title: string;
  startUtcIso: string;
  endUtcIso: string;
  description: string;
  location?: string;
  organizerEmail: string;
  attendeeEmails: string[];
  meetLink?: string;
};

export function buildPublicBookingIcsContent(input: PublicBookingInviteInput): string {
  const descriptionWithMeet = input.meetLink
    ? `${input.description ? `${input.description}\n\n` : ""}Video link: ${input.meetLink}`
    : input.description;
  const locationLine = input.location || input.meetLink || "TBD";

  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "PRODID:-//West Tennessee Consulting//Hub Public Booking//EN",
    "VERSION:2.0",
    "CALSCALE:GREGORIAN",
    "METHOD:REQUEST",
    "BEGIN:VEVENT",
    `UID:${input.uid}`,
    `DTSTAMP:${formatUtcForIcs(new Date().toISOString())}`,
    "STATUS:CONFIRMED",
    "SEQUENCE:0",
    "TRANSP:OPAQUE",
    `DTSTART:${formatUtcForIcs(input.startUtcIso)}`,
    `DTEND:${formatUtcForIcs(input.endUtcIso)}`,
    `SUMMARY:${escapeIcsText(input.title)}`,
  ];

  if (descriptionWithMeet) {
    lines.push(`DESCRIPTION:${escapeIcsText(descriptionWithMeet)}`);
  }
  lines.push(`LOCATION:${escapeIcsText(locationLine)}`);
  lines.push(`ORGANIZER:mailto:${input.organizerEmail}`);
  for (const attendeeEmail of input.attendeeEmails) {
    lines.push(`ATTENDEE;RSVP=TRUE:mailto:${attendeeEmail}`);
  }
  lines.push("END:VEVENT", "END:VCALENDAR");

  return lines.map(foldIcsLine).join("\r\n");
}

export function buildPublicBookingInviteEmailBodies(input: PublicBookingInviteInput): {
  htmlBody: string;
  textBody: string;
  attachments: Array<{ filename: string; contentType: string; content: Buffer }>;
} {
  // Format in the booking's timezone (Central) so recipients see the local
  // appointment time, matching the calendar event — not the UTC/server time.
  // The trailing `zzz` (e.g. "CDT") makes the zone explicit to the recipient.
  const formattedStart = formatInTimeZone(
    parseISO(input.startUtcIso),
    BOOKING_TIMEZONE,
    "EEEE, MMMM d, yyyy, h:mm a zzz"
  );
  const formattedEnd = formatInTimeZone(
    parseISO(input.endUtcIso),
    BOOKING_TIMEZONE,
    "h:mm a zzz"
  );
  const locationLine = input.location || input.meetLink || "TBD";
  const descriptionWithMeet = input.meetLink
    ? `${input.description ? `${input.description}\n\n` : ""}Video link: ${input.meetLink}`
    : input.description;

  const htmlBody = `
    <p>You are invited to <strong>${escapeHtml(input.title)}</strong>.</p>
    <p><strong>When:</strong> ${escapeHtml(formattedStart)} - ${escapeHtml(formattedEnd)}</p>
    <p><strong>Location:</strong> ${escapeHtml(locationLine)}</p>
    ${
      input.meetLink
        ? `<p><strong>Meeting link:</strong> <a href="${escapeHtml(input.meetLink)}">${escapeHtml(input.meetLink)}</a></p>`
        : ""
    }
    ${descriptionWithMeet ? `<p>${escapeHtml(descriptionWithMeet).replace(/\n/g, "<br/>")}</p>` : ""}
    <p>A calendar attachment (.ics) is included — open it to add this meeting to your calendar.</p>
  `;

  const textBody = [
    `Invitation: ${input.title}`,
    `When: ${formattedStart} - ${formattedEnd}`,
    `Location: ${locationLine}`,
    ...(input.meetLink ? [`Meeting link: ${input.meetLink}`] : []),
    ...(descriptionWithMeet ? [`Details: ${descriptionWithMeet}`] : []),
    "",
    "A calendar invite (.ics) is attached.",
  ].join("\n");

  const ics = buildPublicBookingIcsContent(input);
  return {
    htmlBody,
    textBody,
    attachments: [
      {
        filename: "invite.ics",
        contentType: "text/calendar; charset=UTF-8; method=REQUEST",
        content: Buffer.from(ics, "utf-8"),
      },
    ],
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export async function sendPublicBookingInviteEmails(input: PublicBookingInviteInput): Promise<void> {
  const { sendEmailViaSES } = await import("@wtc/email");
  const { htmlBody, textBody, attachments } = buildPublicBookingInviteEmailBodies(input);
  const uniqueAttendees = [...new Set(input.attendeeEmails.map((e) => e.trim().toLowerCase()))].filter(Boolean);

  await sendEmailViaSES({
    to: uniqueAttendees,
    subject: `Invitation: ${input.title}`,
    htmlBody,
    textBody,
    replyTo: input.organizerEmail,
    attachments,
  });
}
